import { TEXT_ROLES } from "../text/typography"
import { EmberRosterRole } from "game-common/models"

// Geometry and colours for one ember roster card. Pure numbers, no pixi -- the widget that draws
// them is ember_roster.ts, and the rows are checked in __tests__/ember_roster_card.test.ts.
//
// A card is a zoomed portrait filling the frame like a background, clipped to an inset rounded rect
// (the margin), with the emoji role badge, the name and the two stat bars overlaid on a scrim. None
// of that is laid out by anything: every row below is a hand-placed constant, stacked at the card's
// foot, which is why the rows are worth a test of their own.

export const SLOT_W = 70
export const SLOT_H = 96
export const PANEL_PAD = 6
export const INNER_MARGIN = 5
export const INNER_RADIUS = 6
// Zoom + vertical framing of the character inside the card (tunable): the character is drawn
// centred on its own origin, so PORTRAIT_Y positions that origin; raise PORTRAIT_SCALE to zoom in.
export const PORTRAIT_SCALE = 1.5
export const PORTRAIT_X = SLOT_W / 2
export const PORTRAIT_Y = 50
export const BADGE_SIZE = TEXT_ROLES.label.fontSize
export const BADGE_X = 4
export const BADGE_Y = 3
export const SCRIM_H = 38
export const NAME_Y = SLOT_H - 32
export const HP_MARGIN = 9
export const HP_Y = SLOT_H - 18
export const HP_H = 5
// The hunger bar rides directly under the HP bar, same width, two pixels of air between them. Only
// recruits have a hunger arc, so the lord's card leaves this row empty (see drawHungerBar).
export const HUNGER_Y = HP_Y + HP_H + 2
export const HUNGER_H = 4
export const NAME_MAX_CHARS = 11
// The tiny name font is 8px; this is the glyph box it actually occupies, which is what the row
// below it has to clear.
export const NAME_H = 11

// Below this the member is in the arc's grace period -- it has left its post and is waiting for food
// that may never come. The bar pulses there (see the widget's animate), because at that point
// feeding it is the only thing keeping it in the camp.
export const HUNGER_URGENT = 0.3
export const HUNGER_PULSE_MS = 900

// Warm food colours, deliberately nothing like the HP bar's green/yellow/red one pixel above:
// amber while the member is working, orange once it goes looking for food, ember red as it runs out
// of patience.
export const hungerColor = (ratio: number): number =>
    ratio > 0.6 ? 0xf0b429 : ratio > HUNGER_URGENT ? 0xe07b39 : 0xd94f3d

// The HP bar's own ramp, here so the hunger colours can be tested against it.
export const hpColor = (ratio: number): number => (ratio > 0.5 ? 0x6fe06f : ratio > 0.25 ? 0xffd23f : 0xff5c5c)

// Emoji role badges (rendered as colour glyphs via canvas Text — do NOT tint these). They live
// here, beside the rest of the card's constants, so the offscreen faction-tracker arrows can show
// the same glyph without importing the whole roster widget (and the renderer graph behind it).
export const ROLE_EMOJI: Record<EmberRosterRole, string> = {
    lord: "👑",
    player: "🧑", // another person in the camp, not a job
    defender: "🛡️",
    forager: "🪓", // wood gathering
    // A stranger at the fire asking to be fed. The badge is what they WANT rather than what they
    // are, because what they are is the one thing the player gets to decide.
    visitor: "🍗",
}

// ===== the visitor card =====
//
// A visitor's card carries a draining ring instead of a hunger bar: a stranger is not hungry in the
// camp's sense (the hunger arc belongs to members who have to be kept fed), they are impatient, and
// the thing worth drawing is how much of that patience is left before they walk back into the dark.
//
// The ring is drawn just inside the card's frame as an arc that unwinds clockwise from twelve
// o'clock, so an untouched visitor is a complete rim and one about to leave is a stub.
export const PATIENCE_INSET = 2.5
export const PATIENCE_W = 2
// Below this the visitor is nearly gone, and the ring joins the HUD's one urgent colour.
export const PATIENCE_URGENT = 0.25

// Gold while they wait, hot orange as it runs out. The gold is the ember UI's own (the wave dial's
// "clearing" colour), so a visitor reads as part of the same warm family as the fire that drew them
// -- and never as the red of something attacking the camp.
export const patienceColor = (ratio: number): number => (ratio > PATIENCE_URGENT ? 0xffb648 : 0xff7a45)

// The damage effect's colours and the one piece of geometry it adds (see roster_damage.ts for the
// timings). The wash sits over the portrait, the ring around the frame, and the ghost segment is
// drawn inside the HP bar between the health just lost and what is left.
export const DAMAGE_WASH = 0xc21a1a
export const DAMAGE_RING = 0xff4d4d
export const DAMAGE_GHOST = 0xffd9d9
export const DAMAGE_NUMBER_COLOR = 0xff5959
export const DAMAGE_NUMBER_SIZE = TEXT_ROLES.title.fontSize
// Where the floating "-N" starts from: over the portrait's chest, clear of both the badge above and
// the name/bars below, so it can rise without colliding with either.
export const DAMAGE_NUMBER_Y = 44
